import type { ExportWorkflowOutput } from '@tunasong/redux'

export interface ExportFailure {
  status: ExportWorkflowOutput['status']
  error: string | null | undefined
  cause: string | null | undefined
  /** "The Band" or "all your data" */
  scope: string
}

/**
 * Container exit codes the export task uses for the two caps. The twin of this constant lives in
 * `backend/workflows/src/workflows/export/exit-codes.ts`; change one and you must change the other.
 * The values avoid Node's reserved 1–14 range so a runtime crash is never read as a cap breach.
 */
const exitCodes = { entityCap: 64, byteCap: 65 } as const

const containerExitCode = (cause: string | null | undefined): number | undefined => {
  if (!cause) {
    return undefined
  }
  try {
    const parsed = JSON.parse(cause) as { Containers?: { ExitCode?: number }[] }
    return parsed.Containers?.find(c => typeof c.ExitCode === 'number')?.ExitCode
  } catch {
    return undefined
  }
}

/** One plain sentence per failure a user can act on; the raw cause is never shown (spec §6) */
export const describeExportFailure = ({ status, error, cause, scope }: ExportFailure): string => {
  if (status === 'ABORTED') {
    return 'The export was cancelled.'
  }
  if (status === 'TIMED_OUT' || error === 'States.Timeout') {
    return 'The export ran out of time. Try again; if it keeps happening, export a smaller folder.'
  }
  const code = containerExitCode(cause)
  if (code === exitCodes.entityCap) {
    return `${scope} has more than the export limit of entities. Export a smaller folder.`
  }
  if (code === exitCodes.byteCap) {
    return `${scope} is larger than the export size limit. Export a smaller folder or remove large recordings first.`
  }
  return `Something went wrong on our side (code ${error ?? 'unknown'}). Try again.`
}
